/**
 * ContactSection — the Omat yhteystiedot contact view (item-4 view #4).
 *
 * Proto-aligned (proto `OmatTiedot.tsx`): a rounded-2xl card of labelled,
 * icon-led contact rows, an inline "Päivitä tietoja" edit affordance, and a
 * bottom data-sync notice linking to the OmaYTK service.
 *
 * Address is split into three labelled rows (Katuosoite / Postinumero /
 * Paikkakunta) reusing the approved verify-field labels, then email + phone —
 * five rows total. Nimi is intentionally OUT (not in MemberProfile; the hub
 * identity card carries the name).
 *
 * Edit (item-4 r1 C4): "Päivitä tietoja" flips the rows to editable inputs with
 * a Peruuta / Tallenna action row (proto inline edit). The edit is LOCAL-STATE
 * ONLY — this build has no backend write, so Tallenna commits to local state and
 * closes to read mode WITHOUT any "saved" claim that would imply a real server
 * write (Constitution P7). Default mode is read-only: no `<input>`/`<textarea>`.
 *
 * Sync notice (item-4 r1 C3): the proto's verbatim bottom disclaimer;
 * "OmaYTK -palvelussa" links to the canonical OmaYTK service (`OMA_YTK_URL` =
 * oma.ytk.fi, owner-confirmed).
 *
 * No raw colour literals (FR-013) — token classes only. Mobile-first, 375 px
 * safe (FR-012).
 *
 * Owned by feature 018-omat-tiedot (proto-parity re-baseline, 026 / D-127).
 * Task: T015 (item-4 r1 C3/C4).
 */

import { type ComponentType, type JSX, useRef, useState } from 'react';
import { validateField } from '../../contact-verification/useContactEditForm.js';
import type { MessageKeys } from '../../i18n/messages/index.js';
import { useTranslate } from '../../i18n/useTranslate.js';
import type { ContactDetailsPatch, MemberProfile } from '../../member-profile/MemberProfilePort.js';
import { useIdempotencyKey } from '../../member-profile/useIdempotencyKey.js';
import { useMemberProfileWriter } from '../../member-profile/useMemberProfile.js';
import { Button } from '../../primitives/Button.js';
import { Input } from '../../primitives/Input.js';
import {
  BuildingIcon,
  EnvelopeIcon,
  type IconProps,
  MailboxIcon,
  MapPinIcon,
  PhoneIcon,
} from '../../shell/icons.js';

// Canonical public OmaYTK service (owner-confirmed). oma.ytk.fi redirects to
// YTK's deployment host; we link the canonical URI, not the env-specific host.
const OMA_YTK_URL = 'https://oma.ytk.fi';

type ContactFieldKey = 'street' | 'postalCode' | 'city' | 'email' | 'phone';

interface ContactValues {
  readonly street: string;
  readonly postalCode: string;
  readonly city: string;
  readonly email: string;
  readonly phone: string;
}

interface FieldDef {
  readonly key: ContactFieldKey;
  readonly icon: ComponentType<IconProps>;
  readonly labelKey: MessageKeys;
  readonly inputType: string;
  readonly autoComplete: string;
}

// Proto row order: the address split (street, postalCode, city) then email, phone.
const FIELD_DEFS: readonly FieldDef[] = [
  {
    key: 'street',
    icon: MapPinIcon,
    labelKey: 'profile.verify.field.street',
    inputType: 'text',
    autoComplete: 'street-address',
  },
  {
    key: 'postalCode',
    icon: MailboxIcon,
    labelKey: 'profile.verify.field.postalCode',
    inputType: 'text',
    autoComplete: 'postal-code',
  },
  {
    key: 'city',
    icon: BuildingIcon,
    labelKey: 'profile.verify.field.city',
    inputType: 'text',
    autoComplete: 'address-level2',
  },
  {
    key: 'email',
    icon: EnvelopeIcon,
    labelKey: 'profile.contact.email',
    inputType: 'email',
    autoComplete: 'email',
  },
  {
    key: 'phone',
    icon: PhoneIcon,
    labelKey: 'profile.contact.phone',
    inputType: 'tel',
    autoComplete: 'tel',
  },
];

// Each row carries a leading field icon (proto OmatTiedot field rows). Token-
// tinted via currentColor; the adjacent <dt> carries the label.
const fieldIconClasses = 'h-5 w-5 shrink-0 text-muted-foreground';

function seedValues(profile: MemberProfile): ContactValues {
  // The IS contact block carries nullable optional fields; the editable rows are
  // plain strings, so an absent value seeds as empty (never a fabricated value).
  return {
    street: profile.contact.streetAddress ?? '',
    postalCode: profile.contact.postalCode ?? '',
    city: profile.contact.city ?? '',
    email: profile.contact.email,
    phone: profile.contact.phone ?? '',
  };
}

/**
 * True when two drafts are the SAME save — the comparison that decides whether a
 * submit reuses its idempotency key (a retry) or mints a fresh one (a new change).
 * Module-scoped and pure so it can be handed to `useIdempotencyKey` before the
 * component body declares its own helpers.
 */
const sameDraft = (a: ContactValues, b: ContactValues): boolean =>
  // Compare on TRIMMED values: the sent payload is trimmed, so a whitespace-only
  // difference is the same save and must reuse the key (FR-006 dedup).
  a.street.trim() === b.street.trim() &&
  a.postalCode.trim() === b.postalCode.trim() &&
  a.city.trim() === b.city.trim() &&
  a.email.trim() === b.email.trim() &&
  a.phone.trim() === b.phone.trim();

export interface ContactSectionProps {
  readonly profile: MemberProfile;
}

export function ContactSection({ profile }: ContactSectionProps): JSX.Element {
  const t = useTranslate();
  const writer = useMemberProfileWriter();
  // Read mode shows the LIVE profile prop directly (035 US1: after a successful
  // write the surface re-reads live — route-on-read, no local mirror, FR-009).
  // `draft` backs the inputs in edit mode; on save it persists via the IS write.
  const display = seedValues(profile);
  const [draft, setDraft] = useState<ContactValues>(display);
  const [isEditing, setIsEditing] = useState(false);
  const [errors, setErrors] = useState<Partial<Record<ContactFieldKey, MessageKeys>>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<MessageKeys | null>(null);
  // In-flight guard as a REF, not `isSubmitting`: two clicks inside one React batch
  // both read the same render's `false`, so a state-only guard lets both writes go
  // out. FR-006 requires the member not be ABLE to double-submit, not merely that
  // the duplicate be absorbed by the back office's idempotency dedup.
  const submittingRef = useRef(false);
  // The one shared key lifecycle (FR-006) — same hook the takeover uses, so the two
  // contact surfaces cannot drift on the rule that defines "the same save".
  const idempotencyKey = useIdempotencyKey<ContactValues>(sameDraft);

  const startEdit = (): void => {
    setDraft(seedValues(profile));
    setErrors({});
    setSubmitError(null);
    idempotencyKey.clear(); // new edit session = new save-intent (matches useContactEditForm.reset)
    setIsEditing(true);
  };
  const cancelEdit = (): void => {
    setSubmitError(null);
    setErrors({});
    idempotencyKey.clear(); // abandoned edit clears the held key, like the takeover surface
    setIsEditing(false);
  };
  const setField = (key: ContactFieldKey, value: string): void => {
    setDraft((previous) => ({ ...previous, [key]: value }));
  };

  const saveEdit = (): void => {
    if (submittingRef.current) {
      return; // in-flight guard — no double-submit
    }
    setSubmitError(null);
    // Client-side per-field validation (FR-007) — reuse the verify-form rules.
    const nextErrors: Partial<Record<ContactFieldKey, MessageKeys>> = {};
    for (const key of ['street', 'postalCode', 'city', 'email', 'phone'] as const) {
      const err = validateField(key, draft[key]);
      if (err !== null) {
        nextErrors[key] = err;
      }
    }
    setErrors(nextErrors);
    if (Object.keys(nextErrors).length > 0) {
      return;
    }

    // Build the patch: the 5 edited fields + the 3 carried UNCHANGED from the
    // current read (preferredName / additionalAddress / countryId — FR-004).
    const patch: ContactDetailsPatch = {
      email: draft.email.trim(),
      phone: draft.phone.trim(),
      streetAddress: draft.street.trim(),
      postalCode: draft.postalCode.trim(),
      city: draft.city.trim(),
      preferredName: profile.contact.preferredName,
      additionalAddress: profile.contact.additionalAddress,
      countryId: profile.contact.countryId,
    };

    const key = idempotencyKey.keyFor(draft);

    submittingRef.current = true;
    setIsSubmitting(true);
    void writer.updateContact(patch, key).then((outcome) => {
      submittingRef.current = false;
      setIsSubmitting(false);
      if (outcome.status === 'ok') {
        // Route-on-read: the writer invalidated the read; the surface re-reads
        // live and displays the authoritative values. Just leave edit mode.
        idempotencyKey.clear();
        setIsEditing(false);
        return;
      }
      setSubmitError(
        outcome.status === 'conflict'
          ? 'profile.verify.saveError.conflict'
          : outcome.status === 'validation'
            ? 'profile.verify.saveError.validation'
            : outcome.status === 'not-found'
              ? 'profile.verify.saveError.notFound'
              : 'profile.verify.saveError.unavailable',
      );
    });
  };

  return (
    <div className="flex flex-col gap-4">
      <div className="overflow-hidden rounded-2xl border border-border bg-card">
        <h3 className="border-b border-border px-4 py-3 text-lg font-semibold text-foreground">
          {t('profile.contact.heading')}
        </h3>

        <dl className="flex flex-col">
          {FIELD_DEFS.map(({ key, icon: Icon, labelKey, inputType, autoComplete }) => {
            const label = t(labelKey);
            return (
              <div key={key} className="border-b border-border px-4 py-3 last:border-b-0">
                {/* The grouping <div>'s direct children must be only <dt>/<dd>
                    (axe definition-list / only-dlitems, PR #282): the leading
                    icon lives INSIDE the <dt>, not a wrapper <div>. */}
                <dt className="flex items-center gap-3 text-sm font-medium text-muted-foreground">
                  <Icon className={fieldIconClasses} aria-hidden="true" />
                  {label}
                </dt>
                <dd className="mt-1 pl-8 text-sm text-foreground">
                  {isEditing ? (
                    <>
                      <Input
                        type={inputType}
                        autoComplete={autoComplete}
                        aria-label={label}
                        value={draft[key]}
                        onChange={(event) => setField(key, event.target.value)}
                      />
                      {errors[key] !== undefined ? (
                        <p className="mt-1 text-sm text-destructive" role="alert">
                          {t(errors[key] as MessageKeys)}
                        </p>
                      ) : null}
                    </>
                  ) : (
                    display[key]
                  )}
                </dd>
              </div>
            );
          })}
        </dl>
      </div>

      {isEditing ? (
        <>
          <div className="flex gap-2">
            <Button variant="outline" className="flex-1" onClick={cancelEdit}>
              {t('profile.verify.cancel')}
            </Button>
            <Button variant="primary" className="flex-1" onClick={saveEdit} disabled={isSubmitting}>
              {t('profile.verify.save')}
            </Button>
          </div>
          {submitError !== null ? (
            // Honest, localized write failure (035 US1, FR-007/FR-008): never a
            // raw error, never a false success (P7 / SC-006).
            <p className="text-sm text-destructive" role="alert">
              {t(submitError)}
            </p>
          ) : null}
        </>
      ) : (
        <Button variant="primary" onClick={startEdit}>
          {t('profile.verify.startEdit')}
        </Button>
      )}

      {/* Proto's bottom data-sync notice (C3). Muted help text; the OmaYTK link
          points at the canonical OmaYTK service (see OMA_YTK_URL). */}
      <p className="text-sm text-muted-foreground">
        {t('profile.contact.syncNotice')}{' '}
        <a
          href={OMA_YTK_URL}
          target="_blank"
          rel="noopener noreferrer"
          className="text-primary underline hover:opacity-80"
        >
          {t('profile.contact.syncNoticeLink')}
        </a>
        .
      </p>
    </div>
  );
}
